import { useModal } from '@bondsports/utils';
import { css } from '@emotion/react';
import { DialogProps, Grid, IconButton, Paper, Tab, Tabs } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { TabContext, TabPanel } from '@material-ui/lab';
import ForgotPassForm from 'components/shared/Dialog/ForgotPassForm/ForgotPassForm';
import LoginForm from 'components/shared/Dialog/LoginForm';
import SignUpForm from 'components/shared/Dialog/SignUpForm';
import { useColors } from 'hooks/useColors';
import { authApi } from 'lib/api/authApi';
import { auth } from 'lib/auth';
import { i18n } from 'lib/i18n';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { userStore } from 'stores/userStore';
import { dialogZIndex } from 'styles/dialog';

import { brandGradientBackgroundCss, mobileOnly, setOpacity } from 'styles/utils';
import { IColors } from 'types/colors';
import { LoginCredentials } from 'types/userSettings';
import Dialog from './Dialog';
import { ErrorText } from './ErrorText';

const KEEP_LOGIN_DIALOG_ON_TOP = 100;

const searchCss = css`
	width: 100%;

	.MuiTabPanel-root {
		padding: 25px 30px 30px;
	}
`;

const loginTabsCss = (colors: IColors) => css`
	.MuiTab-root {
		background-color: ${colors.bg_background_primary};
		color: ${setOpacity(colors.tx_background_overlay || '', 0.5)};
		font-size: 1.6rem;
		height: 70px;

		&.Mui-selected {
			background-color: ${colors.bg_white};
			color: ${colors.tx_background_overlay};
			font-weight: bold;
			box-shadow: 0 0 30px ${setOpacity(colors.black || '', 0.3)};
		}
	}

	.MuiTabs-indicator {
		${brandGradientBackgroundCss(colors)};
		height: 5px;
		top: 0;
	}
`;

const dialogCss = (colors: IColors) => css`
	.MuiBackdrop-root {
		background-color: ${setOpacity(colors.bg_white || '', 0.7)};
	}

	.MuiPaper-root {
		overflow-y: scroll;
	}
`;

const dialogCloseBtnCss = css`
	&.MuiIconButton-root {
		position: absolute;
		right: 3px;
		top: 4px;
		z-index: 1;

		${mobileOnly} {
			right: 2px;
			top: 2px;
			padding: 0;
		}
	}
`;

const tabTitleCss = (colors: IColors) => css`
	&.MuiTab-root.Mui-selected {
		color: ${colors.tx_text_primary};
	}
`;

const fullWidthCss = css`
	width: 100%;
	padding: 10px 0;
`;

enum TabsEnum {
	login = 'login',
	signup = 'signup',
}

interface Props extends DialogProps {
	postFromSignUp?: (success: boolean) => void;
	defaulteEmail?: string;
}

/**
 * Bond Sports Search module.
 * This component supports a variety of use cases on different pages.
 */

function LoginDialog({ postFromSignUp, children, onClose, tabIndex, defaulteEmail, ...restProps }: Props) {
	const { zIndex } = useModal();
	const topMostModal = zIndex + KEEP_LOGIN_DIALOG_ON_TOP;
	const tab = tabIndex === 1 ? TabsEnum.login : TabsEnum.signup;
	const [selectedTab, setSelectedTab] = useState(tab);
	const [loginError, setLoginError] = useState(false);
	const [isForgotPass, setIsForgotPass] = useState(false);
	const [loginSettings, setLoginSettings] = useRecoilState(userStore.loginSettings);
	const { fetchUserSettings } = userStore.useInitUserSettings();
	const { colors } = useColors();

	useEffect(() => {
		setSelectedTab(tab);
	}, [tab]);

	const onSubmit = (values: LoginCredentials) => {
		if (values.email && values.password) {
			authApi
				.login(values.email, values.password)
				.then(() => {
					if (!loginSettings?.isLogin) {
						// set login settings
						setLoginSettings({
							isLogin: true,
							userToken: auth.getToken(),
							userId: auth.getUserId(),
							isCompleteLoginProcess: true,
						});
						// set user settings
						fetchUserSettings().then(() => {
							if (postFromSignUp) {
								postFromSignUp(true);
							} else if (onClose) {
								onClose({}, 'escapeKeyDown');
							}
						});
					}
				})
				.catch(() => {
					setLoginError(true);
				});
		}
	};

	const handleExiting = () => {
		setIsForgotPass(false);
	};

	const setLoginTab = () => setSelectedTab(TabsEnum.login);

	return (
		<Dialog
			css={[dialogCss(colors), dialogZIndex(topMostModal)]}
			onClose={onClose}
			onExiting={handleExiting}
			{...restProps}
		>
			<Grid container justify="flex-end">
				{onClose ? (
					<IconButton
						css={dialogCloseBtnCss}
						aria-label="close"
						onClick={e => {
							onClose(e, 'escapeKeyDown');
							setIsForgotPass(false);
						}}
					>
						<CloseIcon />
					</IconButton>
				) : null}
				{isForgotPass ? (
					<ForgotPassForm setIsForgotPass={setIsForgotPass} setLoginTab={setLoginTab} />
				) : (
					<Paper css={searchCss} elevation={3}>
						<TabContext value={selectedTab}>
							<Tabs
								css={loginTabsCss(colors)}
								value={selectedTab}
								variant="fullWidth"
								onChange={(_, newTab) => setSelectedTab(newTab)}
								aria-label="login module tabs"
							>
								<Tab value={TabsEnum.login} label={i18n.auth.loginBtn} css={tabTitleCss(colors)} />
								<Tab value={TabsEnum.signup} label={i18n.auth.signupBtn} css={tabTitleCss(colors)} />
							</Tabs>
							<TabPanel value={TabsEnum.login}>
								<Grid container direction="column" justify="center" alignItems="center">
									<Grid item xs={12} css={fullWidthCss}>
										<LoginForm onSubmit={onSubmit} setIsForgotPass={setIsForgotPass} defaulteEmail={defaulteEmail} />
									</Grid>
									{loginError && <ErrorText colors={colors} error={i18n.loginDialog.errorMessage} withMargin={false} />}
								</Grid>
							</TabPanel>
							<TabPanel value={TabsEnum.signup}>
								<SignUpForm
									postFromSignUp={postFromSignUp}
									onClose={onClose}
									open
									defaulteEmail={defaulteEmail}
									setIsForgotPass={setIsForgotPass}
								/>
							</TabPanel>
						</TabContext>
					</Paper>
				)}
			</Grid>
			{children}
		</Dialog>
	);
}

export default LoginDialog;
